import { Outlet, createRootRouteWithContext, HeadContent, Scripts, Link } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import appCss from "../styles.css?url";
import { AuthGate } from "@/components/auth/AuthGate";
import { AuthProvider } from "@/context/AuthContext";
import { SelectedFilialProvider } from "@/context/SelectedFilialContext";

interface RouterContext {
  queryClient: QueryClient;
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A página que você procura não existe ou foi movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Ir para o início
          </Link>
        </div>
      </div>
    </div>
  );
}

// Em SPA estático (yarn build:static) o shell HTML já é o index.html.
// O shellComponent só deve existir no fluxo SSR (TanStack Start), caso
// contrário ele é renderizado DENTRO de #root e quebra o app.
const IS_STATIC_SPA = import.meta.env.VITE_USE_HASH_HISTORY === "true";

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "AXIS Comprovantes" },
      {
        name: "description",
        content:
          "AXIS Comprovantes — importe extratos bancários, consulte lançamentos, comprovantes e combinações.",
      },
      { property: "og:title", content: "AXIS Comprovantes" },
      {
        property: "og:description",
        content: "Sistema operacional para importação e conciliação de comprovantes bancários.",
      },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "stylesheet", href: appCss }],
  }),
  ...(IS_STATIC_SPA ? {} : { shellComponent: RootShell }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SelectedFilialProvider>
          <TooltipProvider delayDuration={200}>
            <AuthGate>
              <Outlet />
            </AuthGate>
          </TooltipProvider>
          <Toaster richColors position="top-right" />
        </SelectedFilialProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
